/* eslint-disable brace-style  */
import {createSelector} from 'reselect';
import get from 'lodash.get';

import {getListing, getListingExtended, getTranslations} from './store';
import {getMaxAdultsFromHouseRules} from '../utils/guests';

const getAverageRating = createSelector(
    [getListing], (listing) => {return listing.averageRating;}
);

const getBathrooms = createSelector(
    [getListing], (listing) => {return listing.bathrooms;}
);

const getBathroomStrings = createSelector(
    [getTranslations, getBathrooms],
    (labels, bathrooms) => {
        return {
            bathroomsFull: bathrooms.full && `${labels.bathrooms}: ${bathrooms.full}` || null,
            bathroomsHalf: bathrooms.half && `${labels.halfBaths}: ${bathrooms.half}` || null,
            bathroomsToiletOnly: bathrooms.toiletOnly && `${labels.toiletOnlyRooms}: ${bathrooms.toiletOnly}` || null
        };
    }
);

const getBedrooms = createSelector(
    [getListing], (listing) => {return listing.bedrooms;}
);

const getBearStayReviewData = createSelector(
    [getListing], (listing) => listing.bearStayReview
);

const getBookingType = createSelector(
    [getListing], (listing) => {
        return {
            onlineBookable: listing.onlineBookable,
            instantBook: listing.instantBookable,
            payPerBooking: listing.payPerBooking
        };
    }
);

const getDetailPageUrl = createSelector(
    [getListing], (listing) => {return listing.detailPageUrl;}
);

const getGeoCode = createSelector(
    [getListing], (listing) => {return listing.geoCode;}
);

const getGeography = createSelector(
    [getListing], (listing) => {return listing.geography;}
);

const getGeoRelativeDistance = createSelector(
    [getListing], (listing) => {
        const {geoRelativeDistances} = listing;
        const relativeDistanceString = get(geoRelativeDistances, 'items.0.relations.0.text', null);

        return relativeDistanceString;
    }
);

const getHostType = createSelector(
    [getListing], (listing) => {
        return {
            owner: listing.ownerManaged,
            pm: !listing.ownerManaged,
            ipm: listing.integratedPropertyManager
        };
    }
);

const getLegacyLodgingPolicy = createSelector(
    [getListing], (listing) => {return listing.legacyLodgingPolicy;}
);

const getListingAmenities = createSelector(
    [getListing], (listing) => {return listing.amenities;}
);

const getListingAmenitiesFeatured = createSelector(
    [getListing], (listing) => {return listing.featuredAmenities;}
);

const getAllFeaturedAmenitiesRanked = createSelector(
    [getListing], (listing) => {return listing.allFeaturedAmenitiesRanked;}
);

const getListingCarousel = createSelector(
    [getListing], (listing) => {return listing.carousel;}
);

const getListingHeadline = createSelector(
    [getListing], (listing) => {return listing.headline;}
);

const getListingHouseRules = createSelector(
    [getListing], (listing) => {return listing.houseRules;}
);

// Extended
const getBadges = createSelector(
    [getListingExtended], (listingExtended) => {return listingExtended.badges || [];}
);

const getUnitSpaces = createSelector(
    [getListingExtended], (listingExtended) => {return listingExtended.unitSpaces || {};}
);

const getUnitContentItems = createSelector(
    [getListingExtended], (listingExtended) => {return listingExtended.unitContentItems || [];}
);

const getPropertyContentItems = createSelector(
    [getListingExtended], (listingExtended) => {return listingExtended.propertyContentItems || [];}
);

const getUnitMetadata = createSelector(
    [getListingExtended], (listingExtended) => {return listingExtended.unitMetadata || [];}
);
// Start here
const getListingIds = createSelector(
    [getListing], (listing) => {
        return {
            listingId: listing.listingId,
            propertyId: listing.propertyId
        };
    }
);

const getMaxAdults = createSelector(
    [getListing], (listing) => {
        const {houseRules} = listing;

        return getMaxAdultsFromHouseRules(houseRules);
    }
);

const getMinStayRange = createSelector(
    [getListing], (listing) => {return listing.minStayRange;}
);

const getMultiUnit = createSelector(
    [getListing], (listing) => {
        return {
            isMultiUnit: listing.multiUnitProperty,
            unitName: listing.unitName,
            unitNumber: listing.unitNumber,
            unitDescription: listing.unitDescription
        };
    }
);

const getPetsAllowed = createSelector(
    [getListing], (listing) => {return listing.petsAllowed;}
);

const getPmProfile = createSelector(
    [getListing], (listing) => {return listing.propertyManagerProfile || {};}
);

const getPredictedLocation = createSelector(
    [getListing], (listing) => {return listing.predictedLocation;}
);

const getPropertyName = createSelector(
    [getListing], (listing) => {return listing.propertyName;}
);

const getPropertyType = createSelector(
    [getListing], (listing) => {return listing.propertyType;}
);

const getRateDetails = createSelector(
    [getListing], (listing) => {return listing.rateDetails;}
);

const getRateSummary = createSelector(
    [getListing], (listing) => {return listing.rateSummary || {};}
);

const getRegions = createSelector(
    [getListing], (listing) => {return listing.regions;}
);

const getSearchLandingPages = createSelector(
    [getListing], (listing) => {return listing.searchLandingPages;}
);

const getSleeps = createSelector(
    [getListing], (listing) => {return listing.sleeps;}
);

const getTakesInquiries = createSelector(
    [getListing], (listing) => {return listing.takesInquiries;}
);

const getTaxRate = createSelector(
    [getListing], (listing) => {return listing.taxRate;}
);

const getThumbnailUrl = createSelector(
    [getListing], (listing) => {return listing.thumbnailUrl;}
);

const getAccomodationSummary = createSelector(
    [getListing], (listing) => {return listing.accomodationSummary;}
);

const getArea = createSelector(
    [getListing], (listing) => {
        return {
            area: listing.area,
            areaUnits: listing.areaUnits
        };
    }
);

const getAveragePrice = createSelector(
    [getListing], (listing) => {return listing.averagePrice;}
);

const getContact = createSelector(
    [getListing], (listing) => {return listing.contact;}
);

const getDescription = createSelector(
    [getListing], (listing) => {return (listing.description || '').trim();}
);

const getIpmGuaranteedPricingActive = createSelector(
    [getListing], (listing) => {return listing.ipmGuaranteedPricingActive;}
);

const getPropertyNotes = createSelector(
    [getListing], (listing) => {return listing.propertyNotes;}
);

const getListingRecommendedProperties = createSelector(
    [getListing], (listing) => {return listing.recommendedProperties;}
);

const getListingIsFamilyFriendly = createSelector(
    [getListing], (listing) => {return listing.isFamilyFriendly;}
);

const getVirtualTourUrl = createSelector(
    [getListingCarousel], (listingCarousel) => {
        return listingCarousel.virtualTourUrl;
    }
);

const getIsMatterportVirtualTour = createSelector(
    [getListingCarousel], (listingCarousel) => {
        return listingCarousel.isMatterportVirtualTour;
    }
);

const getMinStay = createSelector(
    [getMinStayRange, getRateDetails],
    (minStayRange, rateDetails) => {
        if (minStayRange) {
            return minStayRange;
        }

        if (rateDetails.rateSections && rateDetails.rateSections.length > 0) {
            const minStayArray = [];

            rateDetails.rateSections.forEach((rateSection) => {
                if (!isNaN(rateSection.minStay)) {minStayArray.push(rateSection.minStay);}
            });

            const minStayLow = Math.min(...minStayArray);
            const minStayHigh = Math.max(...minStayArray);

            return {
                minStayLow,
                minStayHigh
            };
        }

        return {minStayLow: null, minStayHigh: null};
    }
);


export {
    getAccomodationSummary,
    getArea,
    getAveragePrice,
    getAverageRating,
    getBadges,
    getBathrooms,
    getBathroomStrings,
    getBearStayReviewData,
    getBedrooms,
    getBookingType,
    getContact,
    getDescription,
    getDetailPageUrl,
    getGeoCode,
    getGeography,
    getGeoRelativeDistance,
    getHostType,
    getIpmGuaranteedPricingActive,
    getLegacyLodgingPolicy,
    getListingAmenities,
    getListingAmenitiesFeatured,
    getAllFeaturedAmenitiesRanked,
    getListingCarousel,
    getListingHeadline,
    getListingHouseRules,
    getListingIds,
    getListingRecommendedProperties,
    getMaxAdults,
    getMinStay,
    getMinStayRange,
    getMultiUnit,
    getPetsAllowed,
    getPmProfile,
    getPredictedLocation,
    getPropertyContentItems,
    getPropertyName,
    getPropertyNotes,
    getPropertyType,
    getRateDetails,
    getRateSummary,
    getRegions,
    getSearchLandingPages,
    getSleeps,
    getTakesInquiries,
    getTaxRate,
    getThumbnailUrl,
    getUnitSpaces,
    getUnitContentItems,
    getUnitMetadata,
    getListingIsFamilyFriendly,
    getVirtualTourUrl,
    getIsMatterportVirtualTour
};
